import type { TProduct } from "@lexicanon/shared"
import type { ReactNode } from "react"
import type { TMeetingCardAction } from "../../../../components/MeetingCardKebab/interfaces"
import type { IMeetingCardProcessing } from "../../../../lib/pipeline-progress"
import type { IMeetingListItem } from "../../../../lib/types/meeting"

/** DOM id on the dashboard search box, so the smart wrapper can move focus into
 * it on demand (e.g. the Voxcanon header search icon → `?focusSearch`) without
 * threading a React ref across the component boundary. */
export const DASHBOARD_SEARCH_INPUT_ID = "dashboard-omnisearch-input"

/** DOM id on the infinite-scroll sentinel at the bottom of the grouped list. The
 * smart wrapper attaches an IntersectionObserver to it by id (same
 * ref-across-boundary avoidance as the search box) to auto-load the next page. */
export const DASHBOARD_LOAD_MORE_SENTINEL_ID = "dashboard-load-more-sentinel"

export type TGroupMode = "date" | "status" | "people"

/** Client-side filter on the meeting's recording mode (`template_id`). */
export type TRecordingModeFilter = "all" | "full" | "lite" | "error"

/** Cards (the editorial grid) or a compact one-line list. */
export type TListLayout = "cards" | "list"

export interface ISection {
  id: string
  label: string
  items: IMeetingListItem[]
}

export interface IViewProps {
  product: TProduct
  /** Lite chrome: greeting + Start recording, no stats/tabs/filters/merge. Defaults to full. */
  variant?: "full" | "lite"
  /** True while an instant-start (lite CTA) is creating the meeting. */
  isStarting?: boolean
  /** Total meetings before search — drives the Voxcanon empty parchment + "{n} sessions". */
  totalCount: number
  /** First word of the signed-in user's name, for the Lexicanon cockpit greeting.
   * Null → the greeting drops the trailing comma + name (Lexicanon only). */
  firstName: string | null
  /** Meetings whose start falls in the current calendar month — the cockpit's
   * "{n} meetings this month" quiet line (Lexicanon only). */
  monthCount: number
  /** Total recorded minutes across all meetings (unfiltered) — cockpit stat chip. */
  totalMinutes: number
  /** Whether the search-filtered list is empty — drives the "No meetings found" message. */
  filteredIsEmpty: boolean
  /** Grouped, search-filtered meetings (the live hero meeting is already excluded). */
  sections: ISection[]
  /** Flat search-filtered meetings (live hero excluded) — rendered as a single
   * "Meetings" group while an omnisearch is active, in place of the date/status
   * groups. */
  filteredMeetings: IMeetingListItem[]
  /** Per-card finalize progress keyed by meeting id — drives the ghosted card
   * variant while a meeting is still being analyzed. Absent id → normal card. */
  processingById?: ReadonlyMap<string, IMeetingCardProcessing>
  /** The fresh in-progress meeting to promote to the hero card, if any. */
  live: IMeetingListItem | null
  connected: boolean
  isLoading: boolean
  error: string | null
  search: string
  onSearchChange: (value: string) => void
  /** Whether a deep content search is active (query ≥ 2 chars). Splits the
   * dashboard into "Meetings" + "Moments" groups and softens the empty state. */
  momentsActive: boolean
  /** The rendered Moments block (engine hits); null when no content search. */
  momentsSection?: ReactNode
  /** The rendered SearchFilters panel, revealed behind the "Filters" toggle. */
  filtersContent?: ReactNode
  /** Active-filter count for the "Filters" toggle badge. */
  activeFilterCount: number
  group: TGroupMode
  onGroupChange: (group: TGroupMode) => void
  /** Filter the grid by recording mode. Lexicanon only. Defaults to all. */
  recordingModeFilter?: TRecordingModeFilter
  onRecordingModeFilterChange?: (value: TRecordingModeFilter) => void
  /** Cards or compact rows. Defaults to cards. */
  listLayout?: TListLayout
  onListLayoutChange?: (layout: TListLayout) => void
  /** Errors filter: retry every failed meeting currently in the list. */
  onRetryAllFailed?: () => void
  /** Errors filter: confirm, then delete every failed meeting currently in the list. */
  onDeleteAllFailed?: () => void
  /** True while "Try again for all" is walking the failed meetings. */
  retryAllBusy?: boolean
  showHidden: boolean
  onShowHiddenChange: (value: boolean) => void
  /**
   * True when the user is a platform admin OR an owner/admin of the active org.
   * Drives the permanent-vs-soft Delete affordance on each card (a plain member
   * who organized a meeting may only soft-delete it — the API enforces the same)
   * and the "Show hidden" toggle (the list API applies the same gate).
   */
  isOrgAdmin: boolean
  userEmail: string
  /** Open action-item count — 0 hides the subline chip (Lexicanon only). */
  actionsCount: number
  onCardAction: (meetingId: string, action: TMeetingCardAction) => void
  onNewMeeting: () => void
  onMerge: () => void
  onOpenLive: (id: string) => void
  /** More pages exist beyond the loaded set — shows the infinite-scroll sentinel
   * + "Load more" affordance at the bottom of the grouped list. */
  hasMore: boolean
  /** The next page is currently loading — disables the affordance + shows a spinner. */
  isLoadingMore: boolean
  /** Load the next page (fired by the sentinel observer or the button). */
  onLoadMore: () => void
  /** Retry the meetings query — shown as "Try again" on the error state. */
  onRetry?: () => void
  /** Smart RecurringSeriesCard injected by the wrapper (Lexicanon only). */
  recurringSeriesCard?: ReactNode
}
